import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { axiosInstance } from "../axiosInstance";

const apiCache = new Map<string, Promise<any>>();

const DEFAULT_HEADERS = {
  "Content-Type": "application/x-www-form-urlencoded",
};

const getCacheKey = (
  method: string,
  url: string,
  data: unknown = "",
  config: AxiosRequestConfig = {}
): string =>
  JSON.stringify({
    method,
    url,
    data,
    headers: config.headers,
  });

const requestWithCache = <T>(key: string, requestFn: () => Promise<T>): Promise<T> => {
  if (!apiCache.has(key)) {
    const request = requestFn().catch((error) => {
      apiCache.delete(key);
      throw error;
    });

    apiCache.set(key, request);
  }

  return apiCache.get(key) as Promise<T>;
};

const post = <T = any>(
  url: string,
  data?: unknown,
  config: AxiosRequestConfig = {},
  client: AxiosInstance = axiosInstance
): Promise<T> => {
  const mergedConfig: AxiosRequestConfig = {
    headers: DEFAULT_HEADERS,
    ...config,
  };

  const cacheKey = getCacheKey("POST", url, data, mergedConfig);

  return requestWithCache(cacheKey, () =>
    client
      .post(url, data, mergedConfig)
      .then((res) => res.data)
  );
};

const get = <T = any>(
  url: string,
  config: AxiosRequestConfig = {},
  client: AxiosInstance = axiosInstance
): Promise<T> => {
  const cacheKey = getCacheKey("GET", url, "", config);

  return requestWithCache(cacheKey, () =>
    client
      .get(url, config)
      .then((res) => res.data)
  );
};

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const getWithRetryOn429 = <T = any>(
  url: string,
  config: AxiosRequestConfig = {},
  client: AxiosInstance = axiosInstance,
  retries = 3
): Promise<T> => {
  const cacheKey = getCacheKey("GET", url, "", config);

  const attempt = (remaining: number): Promise<T> =>
    client
      .get(url, config)
      .then((res) => res.data)
      .catch((error) => {
        // The upstream API omits CORS headers on its 429 responses, so the
        // browser reports rate-limited requests as a generic network error
        // (error.response is undefined) instead of a 429 status. Treat that
        // shape as rate-limited too so we still retry instead of failing.
        const isRateLimited = error?.response?.status === 429;
        const isLikelyCorsBlocked429 = !error?.response && error?.message === "Network Error";

        if ((isRateLimited || isLikelyCorsBlocked429) && remaining > 0) {
          const retryDelayMs = (retries - remaining + 1) * 1000;
          return delay(retryDelayMs).then(() => attempt(remaining - 1));
        }
        throw error;
      });

  return requestWithCache(cacheKey, () => attempt(retries));
};

export const getCMSContent = (body: unknown) =>
  post(process.env.REACT_APP_GETCMSCONTENT as string, body);

export const getCMSCategory = (body: unknown) =>
  post(process.env.REACT_APP_GETCMSCATEGORY as string, body);

export const getCMSPage = (body: unknown) =>
  post(process.env.REACT_APP_GETCMSPAGE as string, body);

export const getSearch = (body: unknown) =>
  post(process.env.REACT_APP_GETSEARCH as string, body);

export const getVideo = () =>
  get(process.env.REACT_APP_GETVIDEO as string);

export const getTopFaqs = () =>
  get(process.env.REACT_APP_GETTOPFAQS as string);

export const getCirculars = (perPage = 2) =>
  get(
    process.env.REACT_APP_GETCIRCULARS as string,
    {
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_CIRCULARS_BEARER}`,
      },
      params: {
        per_page: perPage,
      },
    },
    axios
  );

export const getNews = () => getWithRetryOn429(process.env.REACT_APP_GETNEWS as string, {}, axios);

export const getArticles = () => get(process.env.REACT_APP_GETARTICLES as string, {}, axios);

export const getHeaderCirculars = (searchTerm = '') =>
  get(
    `${process.env.REACT_APP_MAIN_WEBSITE_URL}wp-json/wp/v2/circular?search=${encodeURIComponent(searchTerm)}&per_page=25&orderby=date`,
    {},
    axios
  );

export const getHeaderBlogs = (searchTerm = '') =>
  get(
    `${process.env.REACT_APP_MAIN_WEBSITE_URL}wp-json/wp/v2/posts?search=${encodeURIComponent(searchTerm)}&per_page=25&orderby=date`,
    {},
    axios
  );

export const getHeaderSearch = (searchTerm = '') =>
  get(
    `${process.env.REACT_APP_HEADER_SEARCH_URL}?action=header_custom_search&search_query=${encodeURIComponent(searchTerm)}`,
    {},
    axios
  );

let visitorIdPromise: Promise<string> | null = null;
const getVisitorId = (): Promise<string> => {
  if (!visitorIdPromise) {
    visitorIdPromise = FingerprintJS.load()
      .then((fp) => fp.get())
      .then((result) => result.visitorId)
      .catch(() => "");
  }
  return visitorIdPromise;
};

let publicIpPromise: Promise<string> | null = null;
const getPublicIp = (): Promise<string> => {
  if (!publicIpPromise) {
    publicIpPromise = axios
      .get("https://api.ipify.org?format=json")
      .then((res) => res.data?.ip || "")
      .catch(() => "");
  }
  return publicIpPromise;
};

const REMOTE_ADDRESS_KEY = "45808f8d2f7abbb2c5d085406583f9c5";

const bufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

let remoteAddressKeyPromise: Promise<CryptoKey> | null = null;
const getRemoteAddressKey = (): Promise<CryptoKey> => {
  if (!remoteAddressKeyPromise) {
    remoteAddressKeyPromise = window.crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(REMOTE_ADDRESS_KEY),
      { name: "AES-GCM" },
      false,
      ["encrypt"]
    );
  }
  return remoteAddressKeyPromise;
};

const encryptRemoteAddress = (raw: string): Promise<string> =>
  getRemoteAddressKey().then((key) => {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    return window.crypto.subtle
      .encrypt({ name: "AES-GCM", iv, tagLength: 128 }, key, new TextEncoder().encode(raw))
      .then((ciphertextWithTag) => `${bufferToBase64(ciphertextWithTag)}|${bufferToBase64(iv)}`);
  });

let remoteAddressHeaderPromise: Promise<string> | null = null;
const buildRemoteAddressHeader = (): Promise<string> => {
  if (!remoteAddressHeaderPromise) {
    remoteAddressHeaderPromise = Promise.all([getVisitorId(), getPublicIp()])
      .then(([visitorId, ip]) => encryptRemoteAddress(`${visitorId}|${ip}`))
      .catch((error) => {
        remoteAddressHeaderPromise = null;
        throw error;
      });
  }
  return remoteAddressHeaderPromise;
};

export const getStockMasterSearch = (searchTerm: string) =>
  buildRemoteAddressHeader().then((remoteAddress) =>
    post(
      process.env.REACT_APP_STOCK_MASTER_URL as string,
      { search: searchTerm },
      {
        headers: {
          "Content-Type": "application/json",
          RemoteAddress: remoteAddress,
        },
      },
      axios
    )
  );
